<script lang="ts">
    import type {RecordHolding} from 'typings/portaro.be.types';
    import type {AuthorityDetailReactivePageData, AuthorityDetailStaticPageData} from '../types';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import {KpAuthorityDetailPageService} from '../kp-authority-detail-page.service';
    import {pipe} from 'core/utils';
    import {encodeUri, loc} from 'shared/utils/pipes';
    import {exists} from 'shared/utils/custom-utils';
    import {getPageContext} from 'shared/layouts/page-context';
    import {onDestroy} from 'svelte';
    import KpVerticalButtonsPanel from 'shared/ui-widgets/vertical-buttons-panel/KpVerticalButtonsPanel.svelte';
    import KpButtonStyleAnchor from 'shared/ui-widgets/button/KpButtonStyleAnchor.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpDropdownButton from 'shared/ui-widgets/dropdown/KpDropdownButton.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import KpDropdownMenuItem from 'shared/ui-widgets/dropdown/KpDropdownMenuItem.svelte';

    const localize = getLocalization();
    const service = getInjector().getByToken<KpAuthorityDetailPageService>(KpAuthorityDetailPageService.serviceName);
    const pageContext = getPageContext<AuthorityDetailStaticPageData, AuthorityDetailReactivePageData>();

    const model = pageContext.staticData.model;
    const hasEditPanel = model.editable || model.deletable || model.copyable || model.filesManageable;
    let recordHoldings: RecordHolding[] | undefined;
    const reactiveDataUnsubscribe = pageContext.reactiveData.subscribe((data) => recordHoldings = data.recordHoldings);
    onDestroy(reactiveDataUnsubscribe);

    const handleEditClick = async () => {
        await service.createRecordEditation(model.record, model.record.fond);
    }
</script>

{#if hasEditPanel}
    <section class="edit-panel" aria-labelledby="edit-panel-label">
        <KpVerticalButtonsPanel>
            <svelte:fragment slot="heading">
                <h2 id="edit-panel-label" class="unset-style">
                    {localize(/* @kp-localization record.RecordManagement */ 'record.RecordManagement')}
                </h2>
            </svelte:fragment>

            <!-- Edit -->
            {#if model.editable}
                <KpButton buttonStyle="primary"
                          isBlock
                          on:click={handleEditClick}>

                    <IconedContent icon="edit">
                        {localize(/* @kp-localization commons.editovat */ 'commons.editovat')}
                    </IconedContent>
                </KpButton>
            {/if}

            <!-- Update with external -->
            {#if model.editable}
                <KpButton isBlock
                          on:click={() => service.updateWithExternalRecord(model.record)}>
                    <IconedContent icon="refresh">
                        {localize(/* @kp-localization detail.UpdateRecordTooltip */ 'detail.UpdateRecordTooltip')}
                    </IconedContent>
                </KpButton>
            {/if}

            <!-- Copy record -->
            {#if model.copyable}
                <KpButton isBlock
                          on:click={() => service.copyRecord(model.record)}>
                    <IconedContent icon="copy-alt">
                        {localize(/* @kp-localization commons.Zkopirovat */ 'commons.Zkopirovat')}
                    </IconedContent>
                </KpButton>
            {/if}

            <!-- Connected files -->
            {#if model.filesManageable}
                <KpButtonStyleAnchor isBlock
                                     href="/media-viewer?rootDirectory={model.record.directoryId}&edit=true&origin={pipe(window.location.href, encodeUri(window))}">
                    <IconedContent icon="file">
                        {localize(/* @kp-localization file.PripojeneSoubory */ 'file.PripojeneSoubory')}
                    </IconedContent>
                </KpButtonStyleAnchor>
            {/if}

            <!-- Record deletion -->
            {#if model.deletable && exists(recordHoldings)}
                <span class="record-deletion-container">
                    {#if recordHoldings.length === 1}
                        <KpButton isBlock
                                  buttonStyle="danger-new"
                                  on:click={() => service.deleteRecordHolding(recordHoldings[0], recordHoldings)}>
                            <IconedContent icon="trash">
                                {localize(/* @kp-localization detail.DeleteAuthority */ 'detail.DeleteAuthority')}
                            </IconedContent>
                        </KpButton>
                    {/if}

                    {#if recordHoldings.length > 1}
                        <KpDropdownButton isBlock buttonStyle="danger-new">
                            <svelte:fragment slot="button">
                                <IconedContent icon="trash">
                                    {localize(/* @kp-localization detail.DeleteAuthority */ 'detail.DeleteAuthority')}
                                    <UIcon icon="angle-small-down"/>
                                </IconedContent>
                            </svelte:fragment>

                            <svelte:fragment slot="menu">
                                {#each recordHoldings as recordHolding(recordHolding.id)}
                                    <KpDropdownMenuItem
                                            on:click={() => service.deleteRecordHolding(recordHolding, recordHoldings)}>
                                        z {pipe(recordHolding.department, loc())}
                                    </KpDropdownMenuItem>
                                {/each}
                            </svelte:fragment>
                        </KpDropdownButton>
                    {/if}
                </span>
            {/if}
        </KpVerticalButtonsPanel>
    </section>
{/if}

<style lang="less">
    :global {
        .edit-panel .record-management-container button {
            white-space: normal;
        }
    }

    .record-deletion-container {
        width: 100%;
    }
</style>